// One column header. A sortable column is the Buttons quiet sort header (SortHeader reveal), whose <th>
// carries aria-sort, whose button keeps the column label as its name and whose tooltip names the sort; an
// unsortable one renders its label, which discloses its full text while truncated; a hidden header keeps a
// screen-reader name. The resize boundary slot sits beside the sort control, never inside it; lockStyle and
// edges place a locked cell.

import React, { useRef } from 'react'
import PropTypes from 'prop-types'

import { SortHeader } from '@CommonComponents/buttons'
import { useTable } from '../context'
import { ACTIONS } from '../types'
import { ClippedValue, Tooltip } from '../tooltip/Tooltip'
import { isResizable } from '../view/view'
import {
  columnName,
  isHeaderHidden,
  sortFailureLabel,
  sortTooltip,
} from './sortLabels'
import { useClipped } from './useClipped'

const NO_EDGES = { start: false, end: false }

const SortableHeader = ({ column, direction, after, ...cell }) => {
  const { config, dispatch } = useTable()
  const labelRef = useRef(null)
  const truncated = useClipped(labelRef, column.label)
  return (
    <SortHeader
      {...cell}
      reveal
      inset
      sort={direction}
      labelRef={labelRef}
      failureLabel={sortFailureLabel(column)}
      wrapButton={button => (
        <Tooltip text={sortTooltip(column, direction, truncated)}>
          {button}
        </Tooltip>
      )}
      after={after}
      onSort={() =>
        dispatch({
          type: ACTIONS.sortToggle,
          key: column.key,
          defaultSort: config.defaultSort,
          reason: 'sort',
        })
      }
    >
      {column.label}
    </SortHeader>
  )
}

SortableHeader.propTypes = {
  column: PropTypes.object.isRequired,
  direction: PropTypes.oneOf(['none', 'ascending', 'descending']).isRequired,
  after: PropTypes.node,
}

export const HeaderCell = ({
  column,
  lockStyle,
  edges = NO_EDGES,
  lead = null,
  grabInside = false,
}) => {
  const { state, slots, layout } = useTable()
  const { ResizeBoundary } = slots
  const hidden = isHeaderHidden(column)
  const sortable = !hidden && Boolean(column.capabilities?.sort)
  const resizable = !(layout && layout.narrow) && isResizable(column)
  // the previous column's resize target reaches into this cell (ResizeBoundary.js Lead)
  const leading =
    Boolean(lead) && !(layout && layout.narrow) && isResizable(lead)
  const { sort } = state.query
  const direction = sortable
    ? sort && sort.key === column.key
      ? sort.direction
      : 'none'
    : undefined
  const className = [
    'me-table-head-cell',
    sortable && 'me-table-sortable',
    resizable && 'me-table-resizable',
    // no next cell carries this column's resize lead, so the whole target stays inside this cell
    resizable && grabInside && 'has-inner-grab',
    leading && 'has-resize-lead',
    lockStyle && 'is-locked',
    edges.start && 'is-lock-start-edge',
    edges.end && 'is-lock-end-edge',
  ]
    .filter(Boolean)
    .join(' ')
  const cell = {
    scope: 'col',
    className,
    style: lockStyle,
    // the content floor reads the header's label by column (geometry/contentFloor.js)
    'data-column-key': column.key,
    'data-lock': lockStyle ? column.lock || undefined : undefined,
  }
  const boundary = (
    <>
      {leading && <ResizeBoundary column={lead} lead />}
      <ResizeBoundary column={column} />
    </>
  )

  if (sortable)
    return (
      <SortableHeader
        {...cell}
        column={column}
        direction={direction}
        after={boundary}
      />
    )

  return (
    <th {...cell}>
      {hidden ? (
        <span className="sr-only">{columnName(column)}</span>
      ) : (
        <ClippedValue text={column.label} className="me-table-head-static" />
      )}
      {boundary}
    </th>
  )
}

HeaderCell.propTypes = {
  column: PropTypes.object.isRequired,
  lockStyle: PropTypes.object,
  edges: PropTypes.shape({ start: PropTypes.bool, end: PropTypes.bool }),
  lead: PropTypes.object,
  grabInside: PropTypes.bool,
}
